import { Customer } from '@thro-types/billing';

import { getVatCheck } from 'api';
import { isISO31661Alpha2 } from 'class-validator';
import { t } from 'i18next';
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';

import { translations } from 'locales/i18n';

interface CustomerValidationSchemaProps {
  /**
   * This is used to check the vatnumber locally via a regex instead of the external service to avoid a rate limit
   */
  shouldVatCheckLocal?: boolean;
}

export function zodAlwaysRefine<T extends z.ZodTypeAny>(zodType: T) {
  return z.preprocess(value => value, zodType);
}

export const customerValidationSchema = ({
  shouldVatCheckLocal = false
}: CustomerValidationSchemaProps = {}) =>
  z
    .object({
      firstName: z.string().trim().optional(),
      lastName: z.string().trim().optional(),
      email: z
        .string({ message: t(translations.validation.required()) })
        .trim()
        .email({ message: t(translations.validation.required()) })
        .min(1, { message: t(translations.validation.required()) }),
      phone: z.string().trim().optional().nullable(),
      companyName: z.string().trim().optional(),
      city: z
        .string({ message: t(translations.validation.required()) })
        .trim()
        .min(1, { message: t(translations.validation.required()) }),
      country: z
        .string({ message: t(translations.validation.required()) })
        .min(1, { message: t(translations.validation.required()) }),
      zip: z
        .string({ message: t(translations.validation.required()) })
        .trim()
        .min(1, { message: t(translations.validation.required()) }),
      address: z
        .string({ message: t(translations.validation.required()) })
        .trim()
        .min(1, { message: t(translations.validation.required()) }),
      ustId: z
        .string({ message: t(translations.validation.required()) })
        .trim()
        .optional()
        .nullable(),
      saveCustomer: z.boolean(),
      customerId: z.string().trim().optional()
    })
    .refine(
      data => {
        const countrycode = (data.country as CountryCode) || 'DE';
        if (data.phone?.trim() && !isValidPhoneNumber(data.phone.trim(), countrycode)) return false;
        return true;
      },
      { message: t(translations.validation.number()), path: ['phone'] }
    )
    .refine(
      data => {
        return data.companyName ? !!data.ustId : true;
      },
      { message: t(translations.validation.required()), path: ['ustId'] }
    )
    .refine(
      data => {
        return data.ustId ? !!data.companyName : true;
      },
      { message: t(translations.validation.required()), path: ['companyName'] }
    )
    .refine(
      async data => {
        if (!data.ustId) return true;
        if (shouldVatCheckLocal) {
          return data.ustId.match(/^[A-Z]*[0-9]*$/);
        }
        if (data.ustId) {
          try {
            return (await getVatCheck(data.ustId.trim())).format_valid;
          } catch (error) {
            return false;
          }
        }
      },
      { message: t(translations.validation.taxId()), path: ['ustId'] }
    )
    .refine(data => (data.ustId && data.companyName) || data.firstName, {
      message: t(translations.validation.required()),
      path: ['firstName']
    })
    .refine(data => (data.ustId && data.companyName) || data.lastName, {
      message: t(translations.validation.required()),
      path: ['lastName']
    })
    .refine(data => isISO31661Alpha2(data.country), {
      message: t(translations.validation.required()),
      path: ['country']
    });

export const useCustomerInitialValues = (
  initialCustomer: (Customer & { isCensored?: boolean; shouldHide?: boolean }) | undefined
) => {
  return initialCustomer
    ? {
        id: initialCustomer.id,
        firstName: initialCustomer.firstName,
        lastName: initialCustomer.lastName,
        email: initialCustomer.email,
        phone: initialCustomer.phone ? initialCustomer.phone : '',
        companyName: initialCustomer.companyName,
        city: initialCustomer.shouldHide && initialCustomer.isCensored ? '' : initialCustomer.city,
        country: initialCustomer.country,
        zip: initialCustomer.shouldHide && initialCustomer.isCensored ? '' : initialCustomer.zip,
        address:
          initialCustomer.shouldHide && initialCustomer.isCensored ? '' : initialCustomer.address,
        ustId: initialCustomer.ustId ? initialCustomer.ustId : '',
        saveCustomer: false,
        customerId: initialCustomer.customerId
      }
    : {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        city: '',
        country: '',
        zip: '',
        address: '',
        ustId: '',
        saveCustomer: false
      };
};
